// Settings -> Configuration: sub-sections and the status vocabulary (documentation/configuration.md).
import type { ConfigSection } from '../../../services/configuration';

export type ConfigurationSubsection = ConfigSection | 'runtime' | 'audit' | 'auth-providers' | 'mfa-policy' | 'delegation' | 'mail' | 'sessions';

export const SUBSECTIONS: { id: ConfigurationSubsection; label: string; hint: string }[] = [
  { id: 'deployment', label: 'Deployment / System', hint: 'This system and the public TEST target' },
  { id: 'auth-providers', label: 'Authentication providers', hint: 'RadioOS password, Microsoft, Google' },
  { id: 'mfa-policy', label: 'MFA policy', hint: 'Who must use multi-factor authentication' },
  { id: 'sessions', label: 'Sessions', hint: 'How long a sign-in lasts: Management Client and Dispatcher' },
  { id: 'delegation', label: 'Roles & delegation', hint: 'Who may assign which role' },
  { id: 'mail', label: 'E-mail', hint: 'Invitation e-mails (Microsoft Graph)' },
  { id: 'entra', label: 'Microsoft Entra', hint: 'Sign-in' },
  { id: 'hetzner', label: 'Hetzner', hint: 'Infrastructure provider: server, network, backup storage' },
  { id: 'simply', label: 'Simply DNS', hint: 'radioos.dk records' },
  { id: 'livekit', label: 'LiveKit', hint: 'Media server' },
  { id: 'onence', label: '1NCE SIM', hint: 'SIM provider: Management API v2 (read-only)' },
  { id: 'runtime', label: 'Runtime services', hint: 'Server, database, Redis' },
  { id: 'audit', label: 'Audit', hint: 'Configuration operations' },
];

export type Tone = 'green' | 'blue' | 'yellow' | 'red' | 'gray';

export const TONES: Record<Tone, string> = {
  green: 'bg-green-900/50 text-green-300 border-green-700',
  blue: 'bg-blue-900/40 text-blue-200 border-blue-700',
  yellow: 'bg-yellow-900/40 text-yellow-200 border-yellow-700',
  red: 'bg-red-900/50 text-red-200 border-red-700',
  gray: 'bg-gray-700 text-gray-300 border-gray-600',
};

// Green is reserved for states backed by evidence (a successful check, a matching DNS answer,
// a reported healthy deployment). "Configured" is never green.
const STATES: Record<string, [string, Tone]> = {
  connected: ['Connected', 'green'],
  evidence_complete: ['Evidence complete', 'green'],
  in_sync: ['In sync', 'green'],
  deployed: ['Deployed', 'green'],
  issued: ['Issued', 'green'],
  healthy: ['Healthy', 'green'],
  provisioned: ['Provisioned', 'green'],
  ok: ['OK', 'green'],
  ACTIVE: ['Active', 'green'],
  running: ['Running', 'green'],
  applied: ['Applied', 'green'],
  enabled: ['Enabled', 'green'],
  resolves: ['Resolves', 'blue'],
  configured: ['Configured', 'blue'],
  reported: ['Reported', 'blue'],
  not_reported: ['Not reported', 'gray'],
  stale_report: ['Stale report', 'yellow'],
  not_checked: ['Not checked', 'gray'],
  not_applicable: ['Not applicable', 'gray'],
  not_configured: ['Not configured', 'yellow'],
  not_available: ['Not available', 'gray'],
  no_iccid: ['No ICCID', 'yellow'],
  self_reported_only: ['Self-reported ICCID only', 'yellow'],
  incomplete: ['Incomplete evidence', 'yellow'],
  lookup_failed: ['Lookup failed', 'red'],
  mismatch: ['Mismatch', 'red'],
  not_provisioned: ['Not provisioned', 'yellow'],
  not_deployed: ['Not deployed', 'yellow'],
  not_issued: ['Not issued', 'yellow'],
  self_signed: ['Self-signed', 'yellow'],
  records_missing: ['Records missing', 'yellow'],
  missing: ['Missing', 'yellow'],
  pending: ['Pending', 'yellow'],
  nxdomain: ['NXDOMAIN', 'yellow'],
  none: ['None', 'yellow'],
  unknown: ['Unknown', 'yellow'],
  warning: ['Warning', 'yellow'],
  'no-renewal-needed': ['Not due', 'blue'],
  renewed: ['Renewed', 'green'],
  blocked: ['Blocked', 'red'],
  critical: ['Critical', 'red'],
  verification_failed: ['Verification failed', 'red'],
  invalid_report: ['Invalid report', 'red'],
  invalid_configuration: ['Invalid configuration', 'red'],
  invalid: ['Invalid', 'red'],
  failed: ['Failed', 'red'],
  unhealthy: ['Unhealthy', 'red'],
  drift: ['Drift', 'red'],
  off: ['Off', 'red'],
  disabled: ['Disabled', 'red'],
  suspended: ['Suspended', 'red'],
  error: ['Error', 'red'],
  expired: ['Expired', 'red'],
};

export function statusPresentation(state: string | undefined): { label: string; tone: Tone } {
  if (!state) return { label: 'Unknown', tone: 'yellow' };
  const known = STATES[state];
  if (known) return { label: known[0], tone: known[1] };
  return { label: state.replace(/_/g, ' '), tone: 'gray' };
}

export const isSubsection = (s: string | undefined): s is ConfigurationSubsection => SUBSECTIONS.some((x) => x.id === s);

// Retired sub-sections and the page that replaced them (old links and bookmarks). OVHcloud was the
// public TEST provider until it moved to Hetzner (2026-10-04); its configuration page is gone.
export const RETIRED_SUBSECTIONS: Readonly<Record<string, ConfigurationSubsection>> = { ovhcloud: 'hetzner' };

export const replacementSubsection = (s: string | undefined): ConfigurationSubsection | undefined =>
  s !== undefined && Object.prototype.hasOwnProperty.call(RETIRED_SUBSECTIONS, s) ? RETIRED_SUBSECTIONS[s] : undefined;
